import { useNavigate } from 'react-router-dom';
import { usePatientActions } from '../../../contexts/PatientActionsContext';
import { ACTION_STYLES } from '../../../utils/constants';
import { getActionRequiredRoute } from '../../../utils/actionRoutes';
import type { OverviewPatient } from '../../../services/patient';

interface StatusActionCardProps {
    patient: OverviewPatient | null;
    patientId: string | null;
}

/**
 * Landing-tab status + action bar — full width, at the top of the Overview
 * tab.
 *
 * Deliberately the loudest thing on the tab: a gray fill against the white
 * cards below it, so the eye lands here first. The emphasis comes from
 * contrast and size rather than a status colour — the action button already
 * carries a per-action colour from ACTION_STYLES, and tinting the bar behind
 * it as well left two colour signals competing for the same meaning.
 *
 * Dispatch mirrors `ActionRequiredCell`'s action button exactly, in two steps:
 * 1. `PatientActionsContext.handleAction` — recognizes modal-type actions
 *    (Start monitoring, Schedule appointment, Upload records, ...) and opens
 *    their modal (mounted globally by `PatientActionsProvider` in `App.tsx`,
 *    so it works from any page, including this one); returns `false` for
 *    anything it doesn't recognize.
 * 2. When `handleAction` returns `false`, the action is a navigation-type one
 *    (Approve case, Submit case, Deliver aligners, Review for
 *    refinement/retention, ...) — `getActionRequiredRoute` resolves its
 *    destination page and we `navigate` there.
 *
 * Earlier versions of this card (and the monitoring tab's now-removed status
 * strip it replaced) only did step 1, so every navigation-type action —
 * "Approve case" included — silently no-opped. That was the bug; the fix is
 * step 2.
 */
function StatusActionCard({ patient, patientId }: StatusActionCardProps) {
    const actions = usePatientActions();
    const navigate = useNavigate();
    const actionRequired = patient?.actionRequired;
    const hasAction = !!actionRequired && actionRequired !== 'None';

    const handleClick = async (): Promise<void> => {
        if (!patientId || !actionRequired) return;
        const handled = await actions.handleAction(actionRequired, patientId);
        if (handled) return;
        const route = getActionRequiredRoute(actionRequired, patientId);
        if (route) navigate(route);
    };

    return (
        <div className="w-full shrink-0 rounded-xl border border-gray-300 bg-gray-100 shadow-sm p-6 flex items-center justify-between gap-6 flex-wrap">
            <div className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    Status
                </span>
                <span className="text-xl font-bold text-gray-900">
                    {patient?.caseStatus ?? '—'}
                </span>
            </div>

            {/* Mirrors the status column: eyebrow on top, the thing itself
                below - so the action reads as the second half of a pair
                rather than a button floating beside a label. */}
            <div className="flex flex-col gap-1.5 items-start sm:items-end">
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    Action required
                </span>
                {hasAction && actionRequired ? (
                    <button
                        type="button"
                        onClick={handleClick}
                        className={`flex items-center justify-center px-5 py-2 h-11 bg-white border-2 ${
                            ACTION_STYLES[actionRequired]?.border ?? 'border-gray-300'
                        } rounded-lg font-bold text-base ${
                            ACTION_STYLES[actionRequired]?.text ?? 'text-gray-800'
                        } shadow-sm hover:bg-gray-50 transition-colors whitespace-nowrap`}
                    >
                        {actionRequired}
                    </button>
                ) : (
                    <span className="text-xl font-bold text-gray-500">No action needed</span>
                )}
            </div>
        </div>
    );
}

export { StatusActionCard };
